import { sanitizeGuestPath, sanitizeGuestSearch } from '@doss/guest-frame'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { BreadcrumbObjectType, useGetEmbedQuery } from '~/common/generated/apollo-operations'
import { usePublishEmbedPage } from '~/common/hooks/useEmbedTitle'
import { DynamicAppBootup } from '~/components/guest-frame/DynamicAppBootup'
import type { GuestFrameRoute, GuestNavigationIntent } from '~/components/guest-frame/GuestFrame'
import { RouteObject } from '~/router/types'
import { RenderEmbed } from './RenderEmbed'

const EMBED_PATH_PREFIX = '/embed/'

// Standalone embed page so a view's "Copy URL" (/embed/:embed_id) resolves, mirroring the
// chart/table routes — here with URL sync: the splat plus the ENTIRE search string are the
// guest router's route space. Host features must not add their own query params under /embed/*.
const EmbedPage = () => {
  const { embed_id: embedId } = useParams<{ embed_id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { data, loading, error } = useGetEmbedQuery({
    variables: { embedId: embedId ?? '' },
    skip: embedId === undefined,
  })

  // Sliced from the RAW pathname: params['*'] re-decodes %2F and corrupts encoded segments.
  const splatStart = location.pathname.indexOf('/', EMBED_PATH_PREFIX.length)
  const guestPath = splatStart === -1 ? '/' : location.pathname.slice(splatStart)
  const path = sanitizeGuestPath(guestPath)
  const search = sanitizeGuestSearch(location.search)

  // Address-bar garbage resets to the embed root rather than rendering a half-trusted location.
  const routeValid = path !== undefined && search !== undefined
  useEffect(() => {
    if (!routeValid && embedId !== undefined) {
      void navigate({ pathname: `${EMBED_PATH_PREFIX}${embedId}`, search: '' }, { replace: true })
    }
  }, [routeValid, embedId, navigate])

  const route = useMemo(
    (): GuestFrameRoute | undefined =>
      path === undefined || search === undefined ? undefined : { path, search, key: location.key },
    [path, search, location.key],
  )

  const onGuestNavigate = useCallback(
    (intent: GuestNavigationIntent): void => {
      // Absolute on purpose: relative navigation under a splat route compounds.
      void navigate(
        {
          pathname: `${EMBED_PATH_PREFIX}${embedId ?? ''}${intent.path === '/' ? '' : intent.path}`,
          search: intent.search,
        },
        { replace: intent.replace },
      )
    },
    [navigate, embedId],
  )

  const onGuestHistoryGo = useCallback(
    (delta: number): void => {
      void navigate(delta)
    },
    [navigate],
  )

  // The shell titles the tab and title bar; the frame's page title is its part to add.
  const [guestTitle, setGuestTitle] = useState('')
  usePublishEmbedPage(
    embedId === undefined
      ? undefined
      : { embedId, embedName: data?.embed.name ?? null, pageTitle: guestTitle },
  )

  if (loading) {
    return <DynamicAppBootup />
  }

  if (embedId === undefined || error !== undefined || data === undefined || route === undefined) {
    return null
  }

  return (
    <RenderEmbed
      key={data.embed.id}
      betaWatermarkDisabled={data.embed.betaWatermarkDisabled}
      embedId={data.embed.id}
      embedName={data.embed.name}
      onGuestHistoryGo={onGuestHistoryGo}
      onGuestNavigate={onGuestNavigate}
      onGuestTitle={setGuestTitle}
      route={route}
      viewContentId={data.embed.viewContent?.id ?? null}
    />
  )
}
export const embedRoute: RouteObject = {
  // The trailing splat is the guest router's path space; an empty splat matches the bare URL.
  path: 'embed/:embed_id/*',
  Component: EmbedPage,
  // objectType + the embed_id param drive the breadcrumbs query (usePage), so the topbar shows
  // the embed's workspace trail instead of falling back to the selected project.
  handle: { objectType: BreadcrumbObjectType.EMBED },
}
